/**
 * One condition as a removable token chip; clicking the body opens the
 * matching editor in a small popover to change it in place.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { X } from 'lucide-react';

import type { StaticTag } from '@/hooks/smswiser/static-tags/useStaticTags';

import type { ListGroupCondition } from '../../list-groups.types';
import { ListsConditionEditor } from './lists';
import { NameConditionEditor } from './NameConditionEditor';
import { TagConditionEditor } from './TagConditionEditor';

const OPERATOR_LABEL: Record<string, string> = {
  contains: 'contains',
  not_contains: "doesn't contain",
  equals: 'equals',
  not_equals: "doesn't equal",
};

export function conditionLabel(condition: ListGroupCondition): string {
  if (condition.type === 'lists') {
    const n = condition.listIds.length;
    return `${n} selected list${n === 1 ? '' : 's'}`;
  }
  if (condition.type === 'tag') return `tag ${condition.tag}`;
  if (condition.type === 'name')
    return `name ${OPERATOR_LABEL[condition.operator]} ${condition.terms
      .map((t) => `"${t}"`)
      .join(' / ')}`;
  return `${condition.field} ${condition.operator} ${condition.value}`;
}

interface Props {
  condition: ListGroupCondition;
  staticTags: StaticTag[];
  countryKey?: string;
  onChange: (next: ListGroupCondition) => void;
  onRemove: () => void;
}

export const ConditionChip: FC<Props> = ({
  condition,
  countryKey,
  staticTags,
  onChange,
  onRemove,
}) => {
  const [editing, setEditing] = useState(false);
  const tagColor =
    condition.type === 'tag'
      ? staticTags.find(
          (t) => t.name.toLowerCase() === condition.tag.toLowerCase(),
        )?.color
      : undefined;

  const apply = (next: ListGroupCondition) => {
    onChange(next);
    setEditing(false);
  };

  return (
    <span className="relative inline-flex items-center gap-1 rounded-lg border border-input bg-card py-1 pl-2.5 pr-1 text-xs font-medium">
      {tagColor ? (
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: tagColor }}
        />
      ) : null}
      <button type="button" onClick={() => setEditing((v) => !v)} className="max-w-56 truncate">
        {conditionLabel(condition)}
      </button>
      <button
        type="button"
        aria-label="Remove condition"
        onClick={onRemove}
        className="rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      >
        <X className="h-3 w-3" />
      </button>
      {editing ? (
        <>
          <button
            type="button"
            aria-label="Close editor"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setEditing(false)}
          />
          <div className="absolute left-0 top-full z-50 mt-1 rounded-lg border border-border bg-popover shadow-lg">
            {condition.type === 'lists' ? (
              <ListsConditionEditor countryKey={countryKey ?? ''} initial={condition} onApply={apply} />
            ) : condition.type === 'name' ? (
              <NameConditionEditor initial={condition} onApply={apply} />
            ) : condition.type === 'tag' ? (
              <TagConditionEditor
                mode={tagColor ? 'static' : 'perf'}
                staticTags={staticTags}
                initial={condition.tag}
                onApply={(tag) => apply({ type: 'tag', tag })}
              />
            ) : null}
          </div>
        </>
      ) : null}
    </span>
  );
};
